'use client'

import { getCrunchesTableColumns } from '@/app/(dashboard)'
import { usePollAndMergeProcessingCrunches } from '@/app/(dashboard)/hooks/usePollAndMergeProcessingCrunches'
import ErrorMessage from '@/app/core/components/ErrorMessage'
import { useTableSort } from '@/app/core/hooks/useTableSort'
import { ALL_TEAMS_OPTION } from '@dc-shared-web/lib/constants'
import { Table, useAuth, useGetMainTableDocuments } from '@/dc-shared-web/src'
import { EmptyState } from '@/dc-shared-web/src/components/features'
import { useOptimizedTeamPlaybooks } from '@/app/core/hooks/useOptimizedTeamPlaybooks'

import { getCubeDocumentPropFromSorting } from '@/dc-shared-web/src/lib/cubeMappers'
import {
  faArchive,
  faFileLines,
  faShare,
} from '@document-crunch/pro-regular-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ReactNode, useMemo } from 'react'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'

const crunchesTableFallbackMap: Record<
  'dashboard' | 'archived' | 'shared',
  {
    icon: IconDefinition
    title: string
    description: ReactNode
  }
> = {
  dashboard: {
    icon: faFileLines,
    title: 'Nothing here yet!',
    description: (
      <>
        Start adding with the <span className='tw-font-semibold'>New</span>{' '}
        button
      </>
    ),
  },
  archived: {
    icon: faArchive,
    title: 'No archived crunches',
    description:
      'When a crunch is archived, it will be safely stored away ready for you to rediscover here.',
  },
  shared: {
    icon: faShare,
    title: 'No shared crunches',
    description: (
      <div>
        <p>Looks like no one has shared any crunches with you yet.</p>
        <p>But when they do, you&apos;ll find them right here.</p>
      </div>
    ),
  },
}

const CrunchesTableFallback = ({
  currentTable,
}: {
  currentTable: 'dashboard' | 'archived' | 'shared'
}) => {
  const fallback = crunchesTableFallbackMap[currentTable]
  return (
    <EmptyState
      icon={fallback.icon}
      title={fallback.title}
      description={fallback.description}
    />
  )
}

const CrunchesTableLayout = ({ children }: { children: ReactNode }) => (
  <div className='tw-flex tw-flex-col tw-gap-3 tw-h-full tw-min-h-main-crunches-table'>
    <h2 className='tw-text-md tw-font-medium'>Crunches</h2>
    <div className='tw-flex tw-flex-col tw-h-full tw-overflow-hidden'>
      {children}
    </div>
  </div>
)

export const CrunchesTable = ({
  selectedTeamId,
  page,
  folderId,
  projectId,
  currentTable,
  emptyState,
  canMoveCrunches = false,
}: {
  selectedTeamId?: string
  page: 'folder' | 'dashboard' | 'project'
  currentTable: 'dashboard' | 'archived' | 'shared'
  folderId?: string
  projectId?: string
  emptyState?: ReactNode
  canMoveCrunches?: boolean
}) => {
  const {
    flags: { versionCompareDiscoverabilityImprovements },
  } = useLDFlags()
  const { user } = useAuth()
  const { sorting, onSortingChange, sortDirection } = useTableSort({
    defaultSort: { id: 'createdAt', desc: true },
    legacyMode: true,
  })
  const mainTableDocumentsParams = {
    currentTable,
    currentTeamId: selectedTeamId,
    currentTeam: selectedTeamId ? null : ALL_TEAMS_OPTION.text,
    visitorTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    sort: getCubeDocumentPropFromSorting(sorting),
    sortDirection,
    page,
    folderId,
    projectId,
  }
  const {
    data: axiosData,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    error,
  } = useGetMainTableDocuments(mainTableDocumentsParams)
  const processingUUIDs =
    axiosData?.pages
      ?.map((page) => {
        return page.data
          .filter(
            (document) =>
              document.status !== 'completed' && document.status !== 'error'
          )
          .map((document) => document.uuid)
      })
      .flat() ?? []

  usePollAndMergeProcessingCrunches(
    processingUUIDs,
    isLoading,
    currentTable,
    mainTableDocumentsParams
  )
  const rows = useMemo(
    () => axiosData?.pages.map((page) => page.data).flat() ?? [],
    [axiosData]
  )

  const crunchUuids = useMemo(() => rows.map((row) => row.uuid), [rows])

  const { data: playbookData, isLoading: isLoadingPlaybooks } =
    useOptimizedTeamPlaybooks(crunchUuids)

  const columns = useMemo(
    () =>
      getCrunchesTableColumns({
        user,
        folderId,
        currentTable,
        canMoveCrunches,
        playbookData,
        versionCompareDiscoverabilityImprovements,
      }),
    [
      user,
      folderId,
      currentTable,
      canMoveCrunches,
      playbookData,
      versionCompareDiscoverabilityImprovements,
    ]
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility: {
        actions: user.teams.length > 0 || user.is_super_admin,
        teamName: user.is_super_admin,
      },
    },
  })

  if (error) {
    return (
      <CrunchesTableLayout>
        <ErrorMessage />
      </CrunchesTableLayout>
    )
  }

  return (
    <CrunchesTableLayout>
      <Table
        table={table}
        onScrollEndReached={fetchNextPage}
        isLoading={isLoading || isFetchingNextPage || isLoadingPlaybooks}
        noData={
          emptyState || <CrunchesTableFallback currentTable={currentTable} />
        }
      />
    </CrunchesTableLayout>
  )
}
